import type { LucideIcon } from "lucide-react";
import type { ComponentProps } from "react";

import { Badge as BaseBadge } from "@/shared/components/primitives/badge";
import { cn } from "@/shared/lib/utils";

type ColocaBadgeProps = ComponentProps<typeof BaseBadge> & {
  /**
   * Use `"circle"` for numeric counters or single-character labels where a pill shape would look oversized —
   * e.g. a notification dot or an unread count. Use `"default"` (or omit) for status labels and text tags.
   */
  shape?: "default" | "circle";
  /**
   * Use to reinforce the badge's meaning with a visual cue — e.g. `<CheckCircle>` for a success state or
   * `<Clock>` for a pending one. Prefer a leading icon when the icon describes the _category_ of the badge.
   */
  icon?: LucideIcon;
  /**
   * Use sparingly — only when the icon signals what happens _after_ interacting with the badge (e.g. `<X>` on
   * a removable tag). Avoid pairing both `icon` and `iconTrailing` at the same time to keep badges compact.
   */
  iconTrailing?: LucideIcon;
};

const shapeStyles = "flex size-6 items-center justify-center rounded-full p-0";

/**
 * Inline label for statuses, categories, and counts.
 *
 * Use `ColocaBadge` to communicate state at a glance — transaction status (pending, completed, failed), user
 * roles, or notification counts. Keep the label short (1–3 words); for longer descriptive text use a tag or
 * chip instead.
 *
 * Pick `shape="circle"` for standalone counters (e.g. unread notifications). Pick `shape="default"` for
 * text-based status pills.
 */
export function ColocaBadge({
  children,
  icon: Icon,
  iconTrailing: IconTrailing,
  className,
  shape,
  ...props
}: ColocaBadgeProps) {
  return (
    <BaseBadge className={cn(className, shape === "circle" ? shapeStyles : {})} {...props}>
      {Icon ? <Icon data-icon="inline-start" /> : null}
      {children}
      {IconTrailing ? <IconTrailing data-icon="inline-end" /> : null}
    </BaseBadge>
  );
}
